/**
 * Русский словарь — исходный текст интерфейса.
 *
 * Он полон по определению: здесь фраза сначала пишется, а потом
 * переводится. Английский словарь сверяется с этим — недостающие ключи
 * видны проверкой, а не наугад.
 *
 * Ключи сгруппированы по экранам. Порядок групп — тот же, в котором
 * человек их встречает: верхняя панель, лента, правила, боты,
 * настройки.
 */

export const ru: Record<string, string> = {
  // --- общее
  'common.close': 'Закрыть',
  'common.back': 'Назад',
  'common.all': 'Все',
  'common.save': 'Сохранить',
  'common.delete': 'Удалить',
  'common.cancel': 'Отмена',
  'common.none': '—',

  // --- верхняя панель
  'top.indicators': 'Индикаторы',
  'top.rules': 'Правила',
  'top.bots': 'Боты',
  'top.settings': 'Настройки',
  'top.connection.connecting': 'подключение',
  'top.connection.open': 'поток',
  'top.connection.closed': 'нет связи',

  // --- лента сигналов
  'feed.title': 'Сигналы',
  'feed.source.all': 'Все',
  'feed.source.rules': 'Индикаторы',
  'feed.source.bots': 'Боты',
  'feed.side.all': 'Все',
  'feed.allSources': 'Все источники',
  'feed.currentTimeframe': 'Только текущий TF',
  'feed.empty': 'Сигналов пока нет',
  'feed.filtered': 'Все сигналы отсеяны фильтрами и порогами',
  'feed.event': 'СИГНАЛ',
  'feed.disconnected': '{name} (отключён)',

  // --- карточка сигнала
  'card.source': 'Источник',
  'card.source.rule': 'Ваше правило',
  'card.source.bot': 'Бот · {version}',
  'card.event': 'Событие',
  'card.symbol': 'Инструмент',
  'card.timeframe': 'Таймфрейм',
  'card.price': 'Цена',
  'card.time': 'Время',
  'card.status': 'Статус',
  'card.details': 'Подробности',
  'card.targets': 'Цели',
  'card.stop': 'Стоп',
  'card.rule.conditions': 'Условия на момент сигнала · v{version}',
  'card.rule.closed': 'Свеча закрылась {time} UTC',
  'card.rule.origin.history':
    'Отметка по истории: так правило сработало бы раньше. Уведомления по ней не приходили.',
  'card.rule.origin.live': 'Это произошло вживую: свеча закрылась, и правило сработало.',
  'card.rule.open': 'Открыть правило на графике',

  // --- правила
  'rules.title': 'Правила',
  'rules.new': '+ Новое правило',
  'rules.empty':
    'Правило описывает, что должно произойти на рынке, чтобы вы об этом узнали. Программировать не нужно: условия собираются из готовых величин.',
  'rules.disabled': 'выключено',
  'rules.otherInstrument': 'другой инструмент',
  'rules.when': 'Когда',
  'rules.instrument': 'Инструмент',
  'rules.fires': 'Срабатывает',
  'rules.fires.onClose': 'на закрытии свечи',
  'rules.version': 'Версия',
  'rules.history': 'История',
  'rules.history.needInstrument': 'откройте этот инструмент, чтобы посчитать',
  'rules.settings': 'Настройки',
  'rules.showOnChart': 'Показывать на графике',
  'rules.showOnChart.hint': 'Выключенное правило не считается и не уведомляет.',
  'rules.notify': 'Уведомлять меня',
  'rules.notify.hint':
    'Приходит, когда свеча закрылась и сигнал подтверждён, — даже если ANZONIX закрыт.',
  'rules.edit': 'Редактировать',
  'rules.remove': 'Удалить правило',

  // --- конструктор
  'builder.name': 'Название правила',
  'builder.when': 'Когда',
  'builder.addCondition': '+ Условие',
  'builder.addGroup': '+ Группа',
  'builder.and': 'И',
  'builder.or': 'ИЛИ',
  'builder.onTimeframe': 'на',
  'builder.sameTimeframe': '{tf} — как у правила',
  'builder.check': 'Проверить на истории',
  'builder.checkTitle': 'Проверка на истории',
  'builder.collapse': 'Свернуть',
  'builder.whatItDoes': 'Что делает это правило',
  'builder.closedBarNote':
    'Сигнал появляется только после закрытия свечи. До этого условие может выполниться и перестать выполняться.',
  'builder.pending': 'Условие выполняется прямо сейчас, но свеча ещё не закрылась.',
  'builder.zero':
    'За показанный период ({bars} свечей) такого не случалось ни разу. Попробуйте смягчить условие — например, увеличить порог.',
  'builder.snapshot.changed':
    'Правило с тех пор изменилось. Здесь показано то, каким оно было, когда возник сигнал.',
  'builder.snapshot.deleted':
    'Правило удалено. Здесь показано то, каким оно было, когда возник сигнал.',
  'builder.snapshot.conditions': 'Условия на тот момент',
  'builder.snapshot.current': 'Показать текущую версию (v{version})',

  // --- величины
  'operand.pick': 'Величина',
  'operand.search': 'Поиск: RSI, объём, тренд…',
  'operand.notFound': 'Ничего не нашлось',
  'operand.multiply': 'Умножить на число',
  'operand.multiply.remove': 'Убрать множитель',
  'operand.shift': 'Взять значение баров назад',
  'operand.shift.remove': 'Убрать сдвиг',
  'operand.back': 'назад',

  // --- боты
  'bots.title': 'Боты',
  'bots.connected': 'Подключённые',
  'bots.empty':
    'Ни один бот не подключён. Бот — это отдельная программа: она запускается своим процессом и сама приходит в ANZONIX.',
  'bots.empty.run': 'Запустить эталонный: {command}',
  'bots.empty.rules':
    'Если нужны свои условия без программирования — это не бот, а правило: их создают в разделе «Правила».',
  'bots.note': 'Бот подключается сам, отдельным процессом: {command}. Терминал показывает тех, кто пришёл.',
  'bots.noStreams': 'потоков нет',
  'bots.andMore': 'и ещё {count}',
  'bots.hidden': 'не показывается в ленте',
  'bots.params': 'Параметры',
  'bots.noParams': 'Бот не объявил настраиваемых параметров',
  'bots.inFeed': 'В ленте',
  'bots.showSignals': 'Показывать сигналы этого бота',
  'bots.showSignals.hint':
    'Это про ленту и график. Бот продолжит работать, даже если снять галочку.',
  'bots.stop': 'Остановить',
  'bots.start': 'Запустить',
  'bots.resetParams': 'Сбросить параметры',
  'bots.status.RUNNING': 'Работает',
  'bots.status.WARMING_UP': 'Прогрев',
  'bots.status.DEGRADED': 'Деградация',
  'bots.status.STOPPED': 'Остановлен',
  'bots.status.ERROR': 'Ошибка',

  // --- настройки
  'settings.title': 'Настройки',
  'settings.tab.appearance': 'Интерфейс',
  'settings.tab.notifications': 'Уведомления',
  'settings.tab.thresholds': 'Пороги',
  'settings.tab.system': 'Система',
  'settings.tab.about': 'О программе',
  'settings.theme': 'Тема',
  'settings.theme.system': 'Как в системе',
  'settings.theme.dark': 'Тёмная',
  'settings.theme.light': 'Светлая',
  'settings.language': 'Язык',
  'settings.language.system': 'Как в системе',
  'settings.language.ru': 'Русский',
  'settings.language.en': 'English',
  'settings.serverTexts':
    'Разбор рынка и объяснения правил приходят с сервера и пока только на русском.',
  'settings.sync.synced': 'сохранено',
  'settings.sync.saving': 'сохраняю…',
  'settings.sync.offline': 'локально',
  'settings.sync.loading': 'загрузка…',
  'settings.system.connection': 'Связь',
  'settings.system.stream': 'Поток данных',
  'settings.system.profile': 'Профиль',
  'settings.system.venue': 'Источник',
  'settings.system.core': 'Сервер',
  'settings.system.coreDown': 'Сервер не отвечает. График и лента показывают то, что успели загрузить.',
  'settings.system.venues': 'Источники',
  'settings.system.subscribers': 'Подписчиков',
  'settings.system.signals': 'Сигналов принято',
  'settings.system.bots': 'Ботов подключено',
  'settings.system.push': 'Push отправлено',
  'settings.system.pushOff': 'канал выключен',
};
